// src/quality-management/components/ProjectSettingsModal.tsx
// Gestão de tipos de atividade do projeto (estilo work item types do Jira)

import React, { useState, useEffect, useCallback } from 'react';
import { Project, ActivityType } from '../services/api/types';
import { apiService } from '../services/api';
import { Modal } from '../../shared/components/ui/Modal';
import { Button } from '../../shared/components/ui/Button';
import { IconButton } from '../../shared/components/ui/IconButton';
import { Input } from '../../shared/components/ui/Input';
import { Textarea } from '../../shared/components/ui/Textarea';
import { Spinner } from '../../shared/components/ui/Spinner';

interface ProjectSettingsModalProps {
  project: Project;
  onClose: () => void;
}

const DEFAULT_COLOR = '#6366f1';

const EditIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4Z" />
  </svg>
);

const TrashIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const ColorSwatch: React.FC<{ value: string; onChange: (v: string) => void; disabled?: boolean }> = ({
  value,
  onChange,
  disabled,
}) => (
  <input
    type="color"
    value={value}
    onChange={(e) => onChange(e.target.value)}
    disabled={disabled}
    className="h-11 w-14 cursor-pointer rounded-lg border border-border bg-surface-1 p-1 disabled:opacity-60"
    aria-label="Cor"
  />
);

const ProjectSettingsModal: React.FC<ProjectSettingsModalProps> = ({ project, onClose }) => {
  const [activityTypes, setActivityTypes] = useState<ActivityType[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newColor, setNewColor] = useState(DEFAULT_COLOR);

  const [editing, setEditing] = useState<ActivityType | null>(null);

  const fetchActivityTypes = useCallback(async () => {
    setLoading(true);
    try {
      const types = await apiService.activityTypes.getProjectActivityTypes(project.id);
      setActivityTypes(Array.isArray(types) ? types : []);
    } catch (e) {
      console.error('Error fetching activity types:', e);
      setActivityTypes([]);
    } finally {
      setLoading(false);
    }
  }, [project.id]);

  useEffect(() => {
    fetchActivityTypes();
  }, [fetchActivityTypes]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    setError('');
    try {
      await apiService.activityTypes.createActivityType({
        project_id: project.id,
        name: newName.trim(),
        description: newDescription.trim() || undefined,
        color: newColor,
      });
      setNewName('');
      setNewDescription('');
      setNewColor(DEFAULT_COLOR);
      await fetchActivityTypes();
    } catch (e: any) {
      setError(e.message || 'Erro ao criar tipo de atividade.');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!editing || !editing.name.trim()) return;
    setSaving(true);
    setError('');
    try {
      await apiService.activityTypes.updateActivityType(editing.id, {
        name: editing.name,
        description: editing.description || undefined,
        color: editing.color,
      });
      setEditing(null);
      await fetchActivityTypes();
    } catch (e: any) {
      setError(e.message || 'Erro ao atualizar tipo de atividade.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    setSaving(true);
    setError('');
    try {
      await apiService.activityTypes.deleteActivityType(id);
      await fetchActivityTypes();
    } catch (e: any) {
      setError(e.message || 'Erro ao deletar tipo de atividade.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Tipos de atividade"
      description={`Categorize as tarefas de ${project.name} com tipos e cores próprios.`}
      size="xl"
      data-testid="project-settings-modal"
      footer={
        <Button type="button" variant="ghost" onClick={onClose}>
          Fechar
        </Button>
      }
    >
      <div className="flex flex-col gap-6">
        {error && (
          <div className="rounded-lg border border-danger/30 bg-danger/10 px-3.5 py-2.5 text-sm text-danger">
            {error}
          </div>
        )}

        {/* Criar novo tipo */}
        <div className="rounded-xl border border-border bg-surface-2 p-4">
          <h4 className="mb-3 text-sm font-semibold text-foreground">Novo tipo de atividade</h4>
          <div className="flex flex-col gap-3">
            <div className="flex items-end gap-3">
              <Input
                label="Nome *"
                placeholder="Ex.: Desenvolvimento, Análise, Testes"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                disabled={saving}
                containerClassName="flex-1"
                data-testid="activity-type-name-input"
              />
              <div className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-foreground">Cor</span>
                <ColorSwatch value={newColor} onChange={setNewColor} disabled={saving} />
              </div>
            </div>
            <Textarea
              label="Descrição"
              placeholder="Descrição opcional"
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              rows={2}
              disabled={saving}
            />
            <div>
              <Button
                type="button"
                onClick={handleCreate}
                loading={saving}
                disabled={saving || !newName.trim()}
                data-testid="activity-type-create"
              >
                Adicionar tipo
              </Button>
            </div>
          </div>
        </div>

        {/* Lista de tipos */}
        <div>
          <h4 className="mb-3 text-sm font-semibold text-foreground">Tipos existentes</h4>
          {loading ? (
            <div className="flex items-center gap-2 py-6 text-sm text-foreground-muted">
              <Spinner size="sm" /> Carregando tipos...
            </div>
          ) : activityTypes.length === 0 ? (
            <p className="rounded-lg border border-dashed border-border bg-surface-2 px-4 py-6 text-center text-sm text-foreground-muted">
              Nenhum tipo de atividade criado ainda.
            </p>
          ) : (
            <div className="flex flex-col gap-2" data-testid="activity-type-list">
              {activityTypes.map((at) => (
                <div
                  key={at.id}
                  className="flex items-center gap-3 rounded-lg border border-border bg-surface-1 px-3.5 py-2.5"
                >
                  {editing?.id === at.id ? (
                    <>
                      <ColorSwatch
                        value={editing.color}
                        onChange={(v) => setEditing({ ...editing, color: v })}
                        disabled={saving}
                      />
                      <Input
                        value={editing.name}
                        onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                        disabled={saving}
                        containerClassName="flex-1"
                      />
                      <Button type="button" size="sm" onClick={handleUpdate} loading={saving} disabled={saving}>
                        Salvar
                      </Button>
                      <Button type="button" size="sm" variant="ghost" onClick={() => setEditing(null)} disabled={saving}>
                        Cancelar
                      </Button>
                    </>
                  ) : (
                    <>
                      <span
                        className="h-5 w-5 shrink-0 rounded-md border border-black/10"
                        style={{ backgroundColor: at.color || DEFAULT_COLOR }}
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-foreground">{at.name}</div>
                        {at.description && (
                          <div className="text-xs text-foreground-muted">{at.description}</div>
                        )}
                      </div>
                      <IconButton
                        aria-label="Editar tipo"
                        size="sm"
                        onClick={() => setEditing({ ...at })}
                        icon={<EditIcon />}
                      />
                      <IconButton
                        aria-label="Deletar tipo"
                        size="sm"
                        onClick={() => handleDelete(at.id)}
                        className="text-danger hover:bg-danger/15"
                        icon={<TrashIcon />}
                      />
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ProjectSettingsModal;
